import * as React from "react";

import type { ModelAssetLoader } from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";
import type { ThreeDPanelState } from "@/shared/state/visualization";
import type { File } from "@/shared/state/visualization/schema/v1/file";
import type {
  Grid,
  ThirdPersonCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  applyJointPose,
  buildPreviewScene,
  restingPose,
  sceneStateWith,
  type JointPose,
  type JointPoseTarget,
  type PreviewScene,
  type PreviewSceneConfig,
} from "./buildPreviewScene";

export interface ModelPreviewState {
  /** The loaded scene, or absent until it has loaded. */
  scene?: PreviewScene;
  /**
   * The panel state to draw: the loaded scene, framed by whatever the user has
   * since made of its camera and grid. Absent until the model has loaded.
   */
  state?: ThreeDPanelState;
  /**
   * The scenery and viewpoint as they currently stand — the values derived from
   * the model on load, until the user changes one. Absent until the model has
   * loaded, because both are measured from it.
   */
  sceneConfig?: PreviewSceneConfig;
  /** Re-frames the camera. A no-op before the model has loaded. */
  setCamera: (camera: ThirdPersonCamera) => void;
  /** Re-styles the floor grid. A no-op before the model has loaded. */
  setGrid: (grid: Grid) => void;
  isLoading: boolean;
  error?: string;
  /**
   * The value a joint currently sits at — radians if it turns, metres if it
   * slides — starting at its constrained default rather than at zero.
   */
  valueOf: (jointName: string) => number;
  /** Poses one joint. A joint that cannot be posed is a no-op. */
  setJoint: (jointName: string, value: number) => void;
  /**
   * Hands over the renderer the pose is applied to, or `null` when there is
   * none.
   *
   * The canvas owns the manager's lifecycle, so it has to pass it back up rather
   * than the other way round. Pose values do **not** flow through this: they are
   * held here so a slider still reads correctly on a machine that could not
   * build a renderer at all.
   */
  onManagerChanged: (manager: JointPoseTarget | null) => void;
}

/**
 * Holds a model file's preview scene, and the pose the user has put it in.
 *
 * All knowledge of how a model becomes a scene — and of what moving a joint
 * means — is in `buildPreviewScene`; this hook only owns the React lifecycle
 * around it: load on file change, discard on unmount, and push the pose into the
 * renderer as it changes.
 *
 * The pose lives here as plain React state, rather than being read back out of
 * the renderer, for two reasons. The renderer is built asynchronously and may
 * never be built at all — a browser without WebGL still gets the joint sidebar,
 * and its sliders still have to show what they were set to. And a value living
 * inside a `requestAnimationFrame` loop cannot make React re-render when it
 * changes. What matters for the flicker this replaced is the *write* path, and
 * that goes straight to component state: the panel state handed to the manager
 * is built once per model and never rewritten.
 *
 * @param loader the same loader the canvas draws through
 * @param file the model to load, or `undefined` while it is still being built
 */
export function useModelPreview(
  loader: ModelAssetLoader,
  file: File | undefined,
): ModelPreviewState {
  const [scene, setScene] = React.useState<PreviewScene | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState(file !== undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [pose, setPose] = React.useState<JointPose>({});
  const [manager, setManager] = React.useState<JointPoseTarget | null>(null);
  /**
   * The camera and grid as the user has them, seeded from the model's own
   * measurements when it loads.
   *
   * Held rather than re-derived, which is the whole point: `framingCamera`
   * computes a *starting* frame, and a value recomputed from the bounding box on
   * every render would silently undo every edit the moment anything else changed.
   * It is state and not a ref because the rendered scene is derived from it.
   */
  const [sceneConfig, setSceneConfig] = React.useState<
    PreviewSceneConfig | undefined
  >(undefined);

  const fileId = file?.fileId;
  const relativePath = file?.relativePath;

  React.useEffect(
    function loadModel() {
      if (fileId === undefined) {
        setScene(undefined);
        setSceneConfig(undefined);
        setIsLoading(false);
        setError(undefined);
        return;
      }

      // A new model has its own joints — and its own size, so its own framing.
      // Neither the previous pose nor the previous camera and grid can carry
      // over.
      let cancelled = false;
      setScene(undefined);
      setSceneConfig(undefined);
      setPose({});
      setError(undefined);
      setIsLoading(true);

      buildPreviewScene(loader, { fileId, relativePath })
        .then((built) => {
          if (cancelled) {
            return;
          }
          setScene(built);
          // The computed framing applies on first open, and on every re-open:
          // the dialog unmounts the hook, so there is no stale camera to carry
          // back in. Set alongside the scene, in the same batch, so no render
          // ever sees one without the other.
          setSceneConfig(built.defaults);
          // The model comes up posed, not blank. A joint whose limits exclude
          // zero cannot be at zero, and an empty pose is exactly the claim that
          // every joint is — so the defaults are applied here, on load, rather
          // than merely offered as a slider's starting position.
          setPose(restingPose(built.joints));
          setIsLoading(false);
        })
        .catch((e: unknown) => {
          if (cancelled || isAbortError(e)) {
            return;
          }
          const failure = e instanceof Error ? e : new Error(String(e));
          ErrorMonitoringService.captureError(failure);
          setError(failure.message);
          setIsLoading(false);
        });

      return () => {
        cancelled = true;
      };
    },
    [fileId, relativePath, loader],
  );

  React.useEffect(
    function poseModel() {
      if (scene === undefined || manager === null) {
        return;
      }
      // Reconciled rather than replayed, so this is also what puts a renderer
      // that arrived late — or a model that has just finished loading — into the
      // pose the user already chose.
      applyJointPose(scene, manager, pose);
    },
    [scene, manager, pose],
  );

  /**
   * What each joint reads before the user moves anything.
   *
   * Also what a joint returns to should anything ever reset one: "back to the
   * default" is this, not zero, and going through the same record is what stops
   * the two drifting apart. Closing the dialog unmounts the hook, so reopening
   * it rebuilds the pose from here too.
   */
  const defaults = React.useMemo(
    () => restingPose(scene?.joints ?? []),
    [scene],
  );

  const valueOf = React.useCallback(
    // The last `?? 0` is only reached by a joint with no value of its own — a
    // fixed or coupled one — which has no slider to read it.
    (jointName: string) => pose[jointName] ?? defaults[jointName] ?? 0,
    [pose, defaults],
  );

  const setJoint = React.useCallback((jointName: string, value: number) => {
    setPose((current) => ({ ...current, [jointName]: value }));
  }, []);

  const onManagerChanged = React.useCallback((next: JointPoseTarget | null) => {
    setManager(next);
  }, []);

  const setCamera = React.useCallback((camera: ThirdPersonCamera) => {
    // Guarded on the config rather than the scene: the two are set together, and
    // there is nothing to re-frame before the model has been measured.
    setSceneConfig((current) => (current ? { ...current, camera } : current));
  }, []);

  const setGrid = React.useCallback((grid: Grid) => {
    setSceneConfig((current) => (current ? { ...current, grid } : current));
  }, []);

  /**
   * The state the canvas draws.
   *
   * Memoized on the scene and the config alone, so the identity only moves when
   * one of them does — `BabylonPreview` pushes `state` into the live renderer
   * whenever its identity changes, and a fresh object every render would restale
   * the model on every keystroke in the joint sidebar.
   */
  const state = React.useMemo(() => {
    if (scene === undefined) {
      return undefined;
    }
    return sceneConfig ? sceneStateWith(scene, sceneConfig) : scene.state;
  }, [scene, sceneConfig]);

  return {
    scene,
    state,
    sceneConfig,
    setCamera,
    setGrid,
    isLoading,
    error,
    valueOf,
    setJoint,
    onManagerChanged,
  };
}
